/**
 * ``useSources`` — UI bridge for the live Sources tab search (PR-4a, M2).
 *
 * Fans out one query to the two corpus-wide BE searches in parallel
 * (articles + case law) via ``SearchPort`` and exposes the two result
 * groups + loading/error. No auto-run on mount — the lawyer drives it from
 * the search box (avoids a spurious BE call + rate-limit hit on tab open).
 *
 * W1: pure orchestration glue. The hook computes no legal state — it reads
 * ports and stores the projected ``SourceResult[]`` the adapter returns.
 * No Zustand store (W5) — search state is ephemeral and pane-local.
 *
 * Faits / Pièces / "Du dossier" are NOT searched here: they are
 * case-scoped and the add-in has no active-case context yet (PR-4c).
 */

import { useCallback, useState } from 'react'
import { getContainer } from '@/container'
import type { SourceResult } from '@/domain/sources/entity'
import type { SourceFilters } from '@/domain/sources/ports'

export interface UseSourcesResult {
  articles: SourceResult[]
  jurisprudence: SourceResult[]
  loading: boolean
  error: Error | null
  /** Run the search. No-op on an empty/whitespace query. */
  search: (query: string, filters?: SourceFilters) => Promise<void>
}

export function useSources(): UseSourcesResult {
  const [articles, setArticles] = useState<SourceResult[]>([])
  const [jurisprudence, setJurisprudence] = useState<SourceResult[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const search = useCallback(
    async (query: string, filters?: SourceFilters) => {
      if (query.trim().length === 0) return
      setLoading(true)
      setError(null)
      try {
        const { search: searchPort } = getContainer()
        // Both searches are independent corpus-wide reads — fan out in
        // parallel so the slower endpoint sets the latency floor.
        const [arts, jps] = await Promise.all([
          searchPort.searchArticles(query, filters),
          searchPort.searchCaseLaw(query, filters),
        ])
        setArticles(arts)
        setJurisprudence(jps)
      } catch (err: unknown) {
        const wrapped = err instanceof Error ? err : new Error(String(err))
        // eslint-disable-next-line no-console
        console.error('[useSources] search failed', wrapped)
        setError(wrapped)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return { articles, jurisprudence, loading, error, search }
}
